/**
 * Collapsible Tabs
 * ========================================================================
 * Automatically collapses tabs that don't fit into a "More" dropdown.
 *
 * Usage — add data-tabs-collapse to any .tabs container:
 *
 *   <div class="tabs" data-tabs-collapse>
 *       <a href="/all/" class="tabs-button tabs-button--active">All</a>
 *       <a href="?status=new" class="tabs-button">New</a>
 *       <a href="?status=active" class="tabs-button">Active</a>
 *       ...
 *   </div>
 *
 * Measures which tabs fit and moves the rest into a dropdown menu appended
 * at the end. The active tab normally never folds into "More": if it would
 * overflow, its width is reserved first, the earlier tabs fill what is left,
 * and the active tab stays visible just before the trigger. Only when the
 * active tab and the trigger together are wider than the strip does the
 * active tab fold too; the trigger then carries active styling, so More
 * stays inside the strip and reachable.
 *
 * Re-runs when the strip's width changes (a ResizeObserver per strip, which
 * covers window resizes, a strip that starts hidden in a closed panel, sheet
 * or tab, and a web font swapping in), after document.fonts.ready, after an
 * htmx swap (strips in or around the swapped element), on sheet:ready (strips
 * in that sheet), and on tabs.js's tab-changed (that strip, so a script
 * .click() on a folded <button data-tab> brings it out of More). A strip that
 * isn't rendered is skipped, since every width reads 0; its observer lays it
 * out once it shows. While the More menu is open, a layout pass waits and
 * re-runs when the menu closes.
 *
 * A More pick is forwarded to the hidden original tab: the item's click is
 * cancelled and original.click() runs instead, so the pick behaves exactly
 * like a click on the tab itself — htmx (hx-get, hx-boost), tabs.js for
 * <button data-tab> tabs, and any capture-phase click guard on the tab all
 * see it. The strip is re-laid out on the next frame so the picked tab comes
 * out of More. If the original was swapped out while the menu was open, the
 * pick goes to the tab with the same data-tab or href in the live strip; if
 * there is none, the pick is cancelled and the strip re-laid out, never a
 * silent full page load.
 * An <a> item keeps the tab's href, and a ctrl, meta, or shift click on it is
 * not forwarded, so "open in new tab" still works.
 *
 * Works with both <a> navigation tabs and <button data-tab> JS tabs.
 * Integrates with the existing dropdown component (dropdowns.js).
 *
 * window._cc.updateTabsCollapse(root) observes and re-lays out every
 * [data-tabs-collapse] strip in root, root itself included. Use it after
 * inserting a strip without an htmx swap (htmx.process() alone never fires
 * htmx:afterSettle).
 */

(function () {
    var OVERFLOW_CLASS = 'tabs-overflow';
    var HIDDEN_CLASS = 'tabs-button--collapsed';
    var MORE_GAP = 8; // breathing room in px

    var lastWidth = new WeakMap();
    var observed = new WeakSet();

    // Only width matters. A layout pass shows and hides the strip's own
    // children, which can change its height (a font-size or wrap change) but
    // not the width of a block-level flex strip, so filtering on width keeps
    // a pass from re-triggering this observer.
    var observer = new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
            var tabs = entry.target;
            if (!tabs.isConnected) {
                // Swapped out — drop it so the observer doesn't hold it
                observer.unobserve(tabs);
                observed.delete(tabs);
                lastWidth.delete(tabs);
                return;
            }
            var box = entry.borderBoxSize && entry.borderBoxSize[0];
            var width = Math.round(box ? box.inlineSize : entry.contentRect.width);
            if (lastWidth.get(tabs) === width) return;
            lastWidth.set(tabs, width);
            updateCollapse(tabs);
        });
    });

    // Records the starting width, so the observer's first notification (which
    // always fires) doesn't run a second pass right after the caller's own.
    function observe(tabs) {
        if (observed.has(tabs)) return;
        observed.add(tabs);
        lastWidth.set(tabs, tabs.offsetWidth);
        observer.observe(tabs, { box: 'border-box' });
    }

    /**
     * Build or return the existing overflow dropdown for a .tabs container.
     */
    function getOrCreateOverflow(tabs) {
        var existing = tabs.querySelector('.' + OVERFLOW_CLASS);
        if (existing) return existing;

        var wrapper = document.createElement('div');
        wrapper.className = 'dropdown ' + OVERFLOW_CLASS;

        var trigger = document.createElement('button');
        trigger.className = 'tabs-button tabs-overflow-trigger dropdown-toggle';
        trigger.setAttribute('data-dropdown', '');
        trigger.setAttribute('data-dropdown-portal', '');
        trigger.setAttribute('type', 'button');
        trigger.textContent = 'More';

        var menu = document.createElement('div');
        menu.className = 'dropdown-menu';
        // A small strip (tabs--card extends tabs--sm) gets a small menu, so
        // its items match the tabs' size.
        if (tabs.matches('.tabs--sm, .tabs--card')) menu.classList.add('dropdown-menu--sm');

        wrapper.appendChild(trigger);
        wrapper.appendChild(menu);

        // Kept as a reference: while open, dropdowns.js portals the menu to
        // <body>, so a querySelector from the wrapper can't find it
        wrapper._menu = menu;

        // dropdowns.js has no close event; watch the menu's .show instead so
        // a layout skipped while it was open runs when it closes, with or
        // without a pick. Deferred a frame, like the pick re-layout below:
        // rebuilding the menu detaches the item a closing click came from.
        new MutationObserver(function () {
            if (menu.classList.contains('show') || !tabs._collapsePending) return;
            requestAnimationFrame(function () {
                if (tabs._collapsePending && tabs.isConnected) updateCollapse(tabs);
            });
        }).observe(menu, { attributes: true, attributeFilter: ['class'] });

        tabs.appendChild(wrapper);

        return wrapper;
    }

    /**
     * Collapse / uncollapse tabs for a single .tabs container.
     */
    function updateCollapse(tabs) {
        // Not rendered (a closed panel, sheet or tab): every width reads 0 and
        // 0 <= 0 would un-fold it. Its ResizeObserver lays it out on show.
        if (!tabs.clientWidth || tabs.offsetParent === null) return;

        // An open More menu is portaled to <body> by dropdowns.js, so it is not
        // inside the strip. Leave the strip as it is and run this pass when the
        // menu closes (see getOrCreateOverflow).
        var openOverflow = tabs.querySelector(':scope > .' + OVERFLOW_CLASS);
        if (openOverflow && openOverflow._menu.classList.contains('show')) {
            tabs._collapsePending = true;
            return;
        }
        tabs._collapsePending = false;

        var buttons = Array.prototype.slice.call(
            tabs.querySelectorAll(':scope > .tabs-button')
        );
        if (!buttons.length) return;

        // 1. Reset — show all tabs, hide overflow dropdown
        buttons.forEach(function (btn) {
            btn.classList.remove(HIDDEN_CLASS);
            btn.style.display = '';
        });
        var overflow = tabs.querySelector('.' + OVERFLOW_CLASS);
        if (overflow) overflow.style.display = 'none';

        // 2. Measure
        var containerWidth = tabs.clientWidth;

        // Estimate overflow trigger width (render briefly to measure)
        overflow = getOrCreateOverflow(tabs);
        overflow.style.display = '';
        var triggerWidth = overflow.offsetWidth + MORE_GAP;
        overflow.style.display = 'none';

        // Sum all tab widths to check if they fit without the More button
        var totalWidth = 0;
        for (var i = 0; i < buttons.length; i++) {
            totalWidth += buttons[i].offsetWidth;
        }

        // Everything fits without overflow — done
        if (totalWidth <= containerWidth) {
            overflow.style.display = 'none';
            overflow.querySelector('.tabs-overflow-trigger')
                .classList.remove('tabs-button--active');
            return;
        }

        // Need overflow — find the split point (reserve space for More trigger)
        var usedWidth = 0;
        var overflowIndex = buttons.length - 1;

        for (var i = 0; i < buttons.length; i++) {
            usedWidth += buttons[i].offsetWidth;
            if (usedWidth > containerWidth - triggerWidth) {
                overflowIndex = i;
                break;
            }
        }

        // The active tab would overflow — reserve its width, then fill the
        // earlier tabs into what is left. It stays visible and out of the menu,
        // unless it and the trigger don't fit side by side: then it folds like
        // any other tab and the trigger shows active styling, since keeping it
        // would push More past the strip's overflow clip.
        var active = null;
        var foldActive = false;
        for (var k = 0; k < buttons.length; k++) {
            if (buttons[k].classList.contains('tabs-button--active')) {
                active = buttons[k];
                break;
            }
        }
        if (active && k >= overflowIndex &&
                active.offsetWidth + triggerWidth > containerWidth) {
            foldActive = true;
        } else if (active && k >= overflowIndex) {
            var budget = containerWidth - triggerWidth - active.offsetWidth;
            usedWidth = 0;
            overflowIndex = k;
            for (var m = 0; m < k; m++) {
                usedWidth += buttons[m].offsetWidth;
                if (usedWidth > budget) {
                    overflowIndex = m;
                    break;
                }
            }
        }

        // 3. Hide overflowing tabs and populate dropdown
        var menu = overflow._menu;
        menu.innerHTML = '';

        for (var j = overflowIndex; j < buttons.length; j++) {
            var btn = buttons[j];
            if (btn === active && !foldActive) continue;
            btn.classList.add(HIDDEN_CLASS);
            btn.style.display = 'none';

            // Clone as dropdown item. A click on it is forwarded to the
            // original tab (see the click handler below).
            var item;
            if (btn.tagName === 'A') {
                item = document.createElement('a');
                item.href = btn.href;
            } else {
                item = document.createElement('button');
                item.setAttribute('type', 'button');
            }
            item.className = 'dropdown-item';
            item._tabsOriginal = btn;
            item._tabsStrip = tabs;

            item.innerHTML = btn.innerHTML;
            if (btn === active) item.classList.add('active');
            menu.appendChild(item);
        }

        // Show overflow dropdown. The trigger carries active styling only when
        // the active tab folded into it.
        overflow.style.display = '';
        overflow.querySelector('.tabs-overflow-trigger')
            .classList.toggle('tabs-button--active', foldActive);
    }

    function layout(tabs) {
        observe(tabs);
        updateCollapse(tabs);
    }

    /**
     * Observe and re-lay out every [data-tabs-collapse] strip in root, root
     * included.
     */
    function updateTabsCollapse(root) {
        root = root || document;
        if (root.matches && root.matches('[data-tabs-collapse]')) layout(root);
        root.querySelectorAll('[data-tabs-collapse]').forEach(layout);
    }

    /**
     * Strips touched by an event on el: those inside it, and the strip that
     * holds it (a swap of a single tab or of the overflow menu's content).
     */
    function updateAround(el) {
        if (!el || !el.querySelectorAll) return;
        var holder = el.parentElement && el.parentElement.closest('[data-tabs-collapse]');
        if (holder) layout(holder);
        updateTabsCollapse(el);
    }

    /**
     * Init: wire up all [data-tabs-collapse] containers.
     */
    function init() {
        // Initial layout. May be empty — a strip can arrive later via an HTMX
        // swap or a boosted navigation, so the listeners below must be wired
        // up regardless of what is on the page right now.
        updateTabsCollapse(document);

        // Widths change when web fonts swap in, without a resize event
        if (document.fonts) document.fonts.ready.then(function () {
            updateTabsCollapse(document);
        });

        // HTMX support — re-run collapse for strips in or around the swap.
        // Strips elsewhere kept their content; a size change reaches them
        // through the ResizeObserver.
        document.body.addEventListener('htmx:afterSettle', function (e) {
            updateAround(e.target);
        });

        // A sheet's body is inserted by script (sheets.js populateSheet), not by
        // an htmx swap, so afterSettle never fires for a strip inside it.
        // Only that sheet's strips: the page behind it hasn't changed.
        document.addEventListener('sheet:ready', function (e) {
            updateAround(e.target);
        });

        // tabs.js moves the active class on any click of a <button data-tab>,
        // including a script .click() on one folded into More (htmx-actions
        // activateAcqsTab, data-tab-target handlers). Re-lay out that strip so
        // the newly active tab comes out of More.
        document.addEventListener('tab-changed', function (e) {
            var tabs = e.target.closest && e.target.closest('[data-tabs-collapse]');
            if (tabs) updateCollapse(tabs);
        });
    }

    // Forward a More pick to its hidden original tab. dropdowns.js restores a
    // portaled menu in the capture phase, before this runs, and closes the menu
    // on its own .dropdown-item handler. tabs.js handles <button data-tab>
    // originals; htmx and click guards handle the rest. A guard that cancels
    // the forwarded click leaves the strip unchanged.
    document.addEventListener('click', function (e) {
        var item = e.target.closest && e.target.closest('.dropdown-item');
        var original = item && item._tabsOriginal;
        if (!original) return;
        if (item.tagName === 'A' && (e.ctrlKey || e.metaKey || e.shiftKey)) return;

        e.preventDefault();
        if (!original.isConnected) {
            // Swapped out while the menu was open: pick the same tab in the
            // live strip, or cancel and re-lay out rather than navigate.
            original = findLiveTab(item._tabsStrip, original);
            if (!original) {
                updateTabsCollapse(document);
                return;
            }
        }
        var tabs = original.closest('[data-tabs-collapse]');
        original.click();

        // Re-lay out so the picked tab, if it is now active, comes out of More.
        // Deferred to the next frame: re-laying out rebuilds the menu and so
        // detaches this item, and later listeners for this same click (an
        // outside-click closer testing e.target.closest(...)) must still see it
        // in place. A tab whose active state arrives with an htmx swap is
        // re-laid out on that swap's settle instead.
        if (tabs) requestAnimationFrame(function () {
            if (tabs.isConnected) updateCollapse(tabs);
        });
    });

    /**
     * The live counterpart of a swapped-out tab: same data-tab, else same
     * href, in the old strip if it is still connected, else the strip that
     * replaced it (same id), else any strip on the page.
     */
    function findLiveTab(strip, stale) {
        var name = stale.getAttribute('data-tab');
        var href = stale.getAttribute('href');
        if (name === null && href === null) return null;
        var roots;
        if (strip && strip.isConnected) {
            roots = [strip];
        } else if (strip && strip.id && document.getElementById(strip.id)) {
            roots = [document.getElementById(strip.id)];
        } else {
            roots = Array.prototype.slice.call(document.querySelectorAll('[data-tabs-collapse]'));
        }
        for (var i = 0; i < roots.length; i++) {
            var tabs = roots[i].querySelectorAll(':scope > .tabs-button');
            for (var j = 0; j < tabs.length; j++) {
                var t = tabs[j];
                if (name !== null ? t.getAttribute('data-tab') === name
                                  : t.getAttribute('href') === href) return t;
            }
        }
        return null;
    }

    window._cc = window._cc || {};
    window._cc.updateTabsCollapse = updateTabsCollapse;

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
